import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/lib/config/constants';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme(theme: Theme): void;
  toggle(): void;
  apply(): void;
}

function resolve(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

function applyClass(resolved: 'light' | 'dark') {
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      resolved: 'light',
      setTheme: (theme) => {
        const resolved = resolve(theme);
        applyClass(resolved);
        set({ theme, resolved });
      },
      toggle: () => {
        const next = get().resolved === 'dark' ? 'light' : 'dark';
        applyClass(next);
        set({ theme: next, resolved: next });
      },
      apply: () => {
        const resolved = resolve(get().theme);
        applyClass(resolved);
        set({ resolved });
      },
    }),
    {
      name: STORAGE_KEYS.theme,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ theme: s.theme }),

      version: 1,
      migrate: (persisted, version) => {
        const state = persisted as { theme?: Theme } | undefined;
        if (version === 0 && state?.theme === 'system') return { theme: 'light' as Theme };
        return state;
      },
    },
  ),
);
